import React from "react";
import { Paper, Box, Typography, Stack } from "@mui/material";
import {
  DnsOutlined,
  PersonOutlineRounded,
  CalendarTodayOutlined,
  DescriptionOutlined,
  CloseRounded,
  FiberManualRecordRounded,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import {
  textPrimary,
  textSecondary,
  errorColor,
  ORDER_STATUS,
} from "../../../constants/consts";

/* ─────────────── Animasyonlar ─────────────── */
const cardIn = keyframes`
  0%   { opacity: 0; transform: translateY(18px) scale(0.94); }
  55%  { opacity: 1; transform: translateY(-3px) scale(1.02); }
  75%  { transform: translateY(1px) scale(0.995); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const unreadPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 ${errorColor}66; transform: scale(1); }
  50%      { box-shadow: 0 0 0 6px ${errorColor}00; transform: scale(1.08); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.65; }
  50%      { opacity: 1; }
`;

/* ─────────────── Duruma göre renk paleti ─────────────── */
const STATUS_THEME = {
  [ORDER_STATUS.POOL]: {
    accent: "#E67E22",
    accentSoft: "#FFF3E0",
    label: "Havuz",
  },
  [ORDER_STATUS.IN_PROGRESS]: {
    accent: "#1976D2",
    accentSoft: "#E3F2FD",
    label: "Kabul",
  },
  [ORDER_STATUS.PENDING]: {
    accent: "#C62828",
    accentSoft: "#FFEBEE",
    label: "Askı",
  },
  [ORDER_STATUS.COMPLETED]: {
    accent: "#2E7D32",
    accentSoft: "#E8F5E9",
    label: "Tamam",
  },
};

const DEFAULT_THEME = {
  accent: "#475569",
  accentSoft: "#F1F5F9",
  label: "Talep",
};

/* ─────────────── Sözleşme renkleri ─────────────── */
function getContractTheme(contract) {
  if (contract === "Var")
    return { fg: "#15803D", bg: "#DCFCE7", border: "#86EFAC" };
  if (contract === "Yok")
    return { fg: "#B91C1C", bg: "#FEE2E2", border: "#FCA5A5" };
  if (contract === "Yeni")
    return { fg: "#1D4ED8", bg: "#DBEAFE", border: "#93C5FD" };
  return { fg: "#475569", bg: "#F1F5F9", border: "#CBD5E1" };
}

/* ─────────────── Meta satırı ─────────────── */
function InfoRow({ icon: Icon, label, value }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ minWidth: 0 }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(15,50,65,0.05)",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 11, color: "#64748B" }} />
      </Box>
      <Typography
        sx={{
          fontSize: 10.5,
          color: textSecondary,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          color: textPrimary,
          fontWeight: 700,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

/* ─────────────── Ana bileşen ─────────────── */
export default function OrderCard({
  order,
  onOpenChat,
  onCancel,
  onPointerDown,
  ghostMode = false,
  index = 0,
}) {
  const statusTheme = STATUS_THEME[order.status] || DEFAULT_THEME;
  const contractTheme = getContractTheme(order.contract);
  const hasUnread = (order.unread || 0) > 0;

  return (
    <Paper
      elevation={0}
      onPointerDown={
        ghostMode
          ? undefined
          : (e) => {
              if (onPointerDown) onPointerDown(e, order);
            }
      }
      onClick={ghostMode ? undefined : onOpenChat}
      sx={{
        position: "relative",
        p: 0,
        mb: 1.25,
        borderRadius: 2,
        // Normal modda "grab", ghost modda "grabbing"
        cursor: ghostMode ? "grabbing" : "grab",
        backgroundColor: "#fff",
        border: "1px solid rgba(15,50,65,0.08)",
        boxShadow: ghostMode
          ? `0 20px 42px ${statusTheme.accent}66`
          : "0 2px 8px rgba(15,50,65,0.06)",
        overflow: "hidden",
        // Sürükleme sırasında metin seçilmesin
        userSelect: ghostMode ? "none" : "auto",
        WebkitUserSelect: ghostMode ? "none" : "auto",
        touchAction: "none", // pointer events için mobilde kaydırmayı engelle
        animation: ghostMode
          ? "none"
          : `${cardIn} 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) ${Math.min(
              index * 0.06,
              0.45,
            )}s both`,
        transition:
          "transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.28s ease, border-color 0.28s ease",
        "&:hover": ghostMode
          ? {}
          : {
              transform: "translateY(-3px)",
              boxShadow: `0 14px 30px ${statusTheme.accent}2E, 0 4px 12px rgba(15,50,65,0.08)`,
              borderColor: `${statusTheme.accent}55`,
              "& .card-chevron": {
                transform: "translateX(3px)",
                color: statusTheme.accent,
                opacity: 1,
              },
            },
      }}
    >
      {/* SOL DURUM ŞERİDİ */}
      <Box
        className="card-accent"
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: statusTheme.accent,
        }}
      />

      {/* ÜST BAŞLIK BANDI */}
      <Box
        sx={{
          pl: 1.5,
          pr: 1,
          pt: 1,
          pb: 0.85,
          background: `linear-gradient(135deg, ${statusTheme.accent}12 0%, ${statusTheme.accentSoft} 100%)`,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          borderBottom: `1px solid ${statusTheme.accent}1F`,
        }}
      >
        {/* Status label */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.4,
            px: 0.75,
            py: 0.2,
            borderRadius: 10,
            backgroundColor: statusTheme.accent,
            color: "#fff",
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            flexShrink: 0,
            boxShadow: `0 2px 6px ${statusTheme.accent}55`,
          }}
        >
          <FiberManualRecordRounded
            sx={{
              fontSize: 7,
              animation:
                order.status === ORDER_STATUS.IN_PROGRESS
                  ? `${glowPulse} 1.4s ease-in-out infinite`
                  : "none",
            }}
          />
          {statusTheme.label}
        </Box>

        {/* Unread */}
        {hasUnread && (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10.5,
              fontWeight: 900,
              backgroundColor: errorColor,
              color: "#fff",
              flexShrink: 0,
              animation: `${unreadPulse} 1.8s ease-in-out infinite`,
            }}
          >
            {order.unread}
          </Box>
        )}

        {/* Başlık */}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            fontWeight: 800,
            color: textPrimary,
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={order.title}
        >
          {order.title}
        </Typography>

        {/* İptal butonu — drag'i engellemesi için data-no-drag */}
        {!ghostMode && (
          <Box
            data-no-drag
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onCancel?.(order);
            }}
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#94A3B8",
              backgroundColor: "rgba(255,255,255,0.6)",
              flexShrink: 0,
              transition:
                "background 0.2s ease, color 0.2s ease, transform 0.2s ease",
              "&:hover": {
                backgroundColor: "#FEE2E2",
                color: "#B91C1C",
                transform: "scale(1.12) rotate(90deg)",
              },
              "&:active": { transform: "scale(0.9) rotate(90deg)" },
            }}
            title="İptal et"
          >
            <CloseRounded sx={{ fontSize: 13 }} />
          </Box>
        )}
      </Box>

      {/* META BİLGİLER */}
      <Box sx={{ pl: 2, pr: 1.25, pt: 1, pb: 0.75 }}>
        <Stack spacing={0.6}>
          <InfoRow
            icon={DnsOutlined}
            label="Sunucu"
            value={order.sunucuKodu || "-"}
          />
          <InfoRow
            icon={PersonOutlineRounded}
            label="Ad"
            value={order.username || "-"}
          />
          <InfoRow
            icon={CalendarTodayOutlined}
            label="Tarih"
            value={order.createdAt || "-"}
          />
        </Stack>
      </Box>

      {/* ALT: SÖZLEŞME + AÇ */}
      <Box
        sx={{
          pl: 2,
          pr: 1,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.75,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 0.75,
            py: 0.3,
            borderRadius: 1,
            backgroundColor: contractTheme.bg,
            border: `1px solid ${contractTheme.border}`,
            minWidth: 0,
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 11, color: contractTheme.fg }} />
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 800,
              color: contractTheme.fg,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Sözleşme: {order.contract || "-"}
          </Typography>
        </Box>

        <Box
          className="card-chevron"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25,
            color: textSecondary,
            opacity: 0.55,
            transition:
              "transform 0.25s ease, color 0.25s ease, opacity 0.25s ease",
            fontSize: 10.5,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          <span>Aç</span>
          <Box component="span" sx={{ fontSize: 13, lineHeight: 1 }}>
            ›
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
